package com.nonopn.skydome.common.app.pages.probes

import com.nonopn.skydome.common.app.api.impl.AccountAction
import com.nonopn.skydome.common.app.api.impl.AccountStatus
import com.nonopn.skydome.common.app.api.impl.ApiProbe
import com.nonopn.skydome.common.app.api.impl.DemoState
import com.nonopn.skydome.common.app.api.impl.DemoStatus
import com.nonopn.skydome.common.app.api.impl.ProbeSource
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.asin
import kotlin.math.ceil
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.round
import kotlin.math.sin
import kotlin.math.sqrt

// Pure logic of the probe creation / 30-day trial / payment feature (no UI, no network).

/** Feature flag: the new buttons stay hidden for regular accounts until the backend routes exist. */
object ProbeCreationFlag {
    // Flip to true once the backend routes (docs/probe-creation-api.md) are live in production
    const val ENABLED_FOR_EVERYONE = false

    fun isVisible(isAdmin: Boolean, enabledForEveryone: Boolean = ENABLED_FOR_EVERYONE) =
        enabledForEveryone || isAdmin
}

const val TRIAL_DURATION_DAYS = 30
const val SECONDS_PER_DAY = 86_400L
const val PROBE_RADIUS_KM = 100

// J-7 and J-1 reminders
const val TRIAL_BANNER_WEEK_DAYS = 7
const val TRIAL_BANNER_LAST_DAY = 1

// The Nsg certificate can be bought per unit / per pack: it is never blocked during a trial for now
const val BLOCK_NSG_PDF_DURING_TRIAL = false

enum class ProbeStatus { Trial, Unpaid, Active, Expired }

/** Raw fields of a probe, as returned by the server (all optional for older servers). */
data class ProbeStatusInput(
    val status: String? = null,
    val isDemo: Boolean = false,
    val enabled: Boolean? = null,
    val validUntilSecondsUtc: Long? = null,
    val trialEndsAtSecondsUtc: Long? = null,
) {
    /** End of the trial: the dedicated field when present, else validUntilSecondsUtc of a demo probe. */
    val trialEnd: Long? get() = trialEndsAtSecondsUtc ?: validUntilSecondsUtc.takeIf { isDemo }
}

/** True for a probe the server reports as deleted (to be hidden everywhere). */
fun isProbeDeleted(status: String?) = status?.trim()?.lowercase() == "deleted"

/**
 * Resolves the status. The server (lpsmanager) sends Active / Inactive / Deleted plus isDemo and
 * validUntilSecondsUtc: Active = active (or trial when isDemo), Inactive + isDemo = trial / expired trial,
 * Inactive without demo = unpaid. Legacy values (trial / unpaid / active / expired) and the fallback on
 * isDemo / enabled / validUntilSecondsUtc are kept for older servers.
 */
fun resolveProbeStatus(input: ProbeStatusInput, nowSeconds: Long): ProbeStatus {
    val trialEnds = input.trialEnd
    when (input.status?.trim()?.lowercase()) {
        "active" -> if (input.isDemo) return demoStatus(trialEnds, nowSeconds, enabled = null)
        "inactive" -> return if (input.isDemo) {
            demoStatus(trialEnds, nowSeconds, enabled = input.enabled)
        } else {
            ProbeStatus.Unpaid
        }
        "deleted" -> return ProbeStatus.Expired
    }
    val fromServer = when (input.status?.trim()?.lowercase()) {
        "trial" -> ProbeStatus.Trial
        "unpaid" -> ProbeStatus.Unpaid
        "active" -> ProbeStatus.Active
        "expired" -> ProbeStatus.Expired
        else -> null
    }
    if (fromServer != null) {
        // A trial whose end date has passed is expired even if the server did not flip the status yet
        return if (fromServer == ProbeStatus.Trial && trialEnds != null && trialEnds <= nowSeconds) {
            ProbeStatus.Expired
        } else {
            fromServer
        }
    }
    if (input.isDemo) return demoStatus(trialEnds, nowSeconds, input.enabled)
    val validUntil = input.validUntilSecondsUtc
    return when {
        validUntil != null && validUntil <= nowSeconds -> ProbeStatus.Expired
        input.enabled == false -> ProbeStatus.Unpaid
        else -> ProbeStatus.Active
    }
}

/** Demo probe: trial while the end date is in the future, expired once passed (or disabled without any date). */
private fun demoStatus(trialEnds: Long?, nowSeconds: Long, enabled: Boolean?): ProbeStatus = when {
    trialEnds != null && trialEnds <= nowSeconds -> ProbeStatus.Expired
    enabled == false && trialEnds == null -> ProbeStatus.Expired
    else -> ProbeStatus.Trial
}

/** Whole days left (rounded up, never negative). */
fun daysRemaining(nowSeconds: Long, endsAtSeconds: Long): Int {
    val left = endsAtSeconds - nowSeconds
    if (left <= 0L) return 0
    return ceil(left.toDouble() / SECONDS_PER_DAY).toInt()
}

/** Progress of the 30-day trial between 0 and 1 (progress bar). */
fun trialProgress(nowSeconds: Long, endsAtSeconds: Long): Float {
    val total = TRIAL_DURATION_DAYS * SECONDS_PER_DAY
    val left = (endsAtSeconds - nowSeconds).coerceIn(0L, total)
    return ((total - left).toDouble() / total).toFloat()
}

enum class TrialBannerKind { Week, LastDay }

data class TrialBanner(val kind: TrialBannerKind, val daysLeft: Int)

/** Reminder at J-7 (from 7 days to 2 days left) and J-1 (last day). Null otherwise. */
fun trialBannerFor(daysLeft: Int): TrialBannerKind? = when {
    daysLeft <= 0 -> null
    daysLeft <= TRIAL_BANNER_LAST_DAY -> TrialBannerKind.LastDay
    daysLeft <= TRIAL_BANNER_WEEK_DAYS -> TrialBannerKind.Week
    else -> null
}

/** Banner of the trial probe ending first (only probes whose status is Trial). */
fun trialBanner(probes: List<ProbeStatusInput>, nowSeconds: Long): TrialBanner? {
    return probes
        .filter { resolveProbeStatus(it, nowSeconds) == ProbeStatus.Trial }
        .mapNotNull { it.trialEnd }
        .minOrNull()
        ?.let { endsAt ->
            val days = daysRemaining(nowSeconds, endsAt)
            trialBannerFor(days)?.let { TrialBanner(it, days) }
        }
}

enum class ProbeAction { None, Upgrade, FinalizePayment }

/** Button of a probe card: convert a trial (or reactivate an expired trial), or finalize an unpaid probe. */
fun probeAction(input: ProbeStatusInput, status: ProbeStatus): ProbeAction = when (status) {
    ProbeStatus.Trial -> ProbeAction.Upgrade
    ProbeStatus.Unpaid -> ProbeAction.FinalizePayment
    ProbeStatus.Expired ->
        if (input.isDemo || input.trialEnd != null) ProbeAction.Upgrade else ProbeAction.None
    ProbeStatus.Active -> ProbeAction.None
}

/** A probe counts as "usable" (no promo needed) when it is active or in trial. */
fun isUsableStatus(status: ProbeStatus) = status == ProbeStatus.Active || status == ProbeStatus.Trial

/** The promo card of the dashboard / Nsg screen is shown while the account has no active or trial probe. */
fun needsProbePromo(statuses: List<ProbeStatus>) = statuses.none { isUsableStatus(it) }

data class ProbeCoverage(
    val latitude: Double,
    val longitude: Double,
    val radiusKm: Int,
    val status: ProbeStatus,
)

fun haversineKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
    val r = 6371.0
    val dLat = (lat2 - lat1) * PI / 180.0
    val dLon = (lon2 - lon1) * PI / 180.0
    val a = sin(dLat / 2).pow(2) +
        cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) * sin(dLon / 2).pow(2)
    return 2 * r * asin(sqrt(a))
}

/**
 * PDF export is blocked when every usable probe covering the site is a trial probe.
 * No covering probe at all: nothing is blocked (behaviour unchanged).
 */
fun isPdfBlocked(latitude: Double, longitude: Double, probes: List<ProbeCoverage>): Boolean {
    val covering = probes.filter {
        isUsableStatus(it.status) && haversineKm(latitude, longitude, it.latitude, it.longitude) <= it.radiusKm
    }
    return covering.isNotEmpty() && covering.all { it.status == ProbeStatus.Trial }
}

data class ProbeWithPosition(val latitude: Double, val longitude: Double, val input: ProbeStatusInput)

/** What a site-related screen needs: promo card (no usable probe) and PDF lock (trial coverage only). */
data class ProbeAccess(
    val loaded: Boolean = false,
    val needsPromo: Boolean = false,
    val pdfBlocked: Boolean = false,
)

fun computeProbeAccess(
    probes: List<ProbeWithPosition>,
    siteLatitude: Double,
    siteLongitude: Double,
    nowSeconds: Long
): ProbeAccess {
    val statuses = probes.map { resolveProbeStatus(it.input, nowSeconds) }
    val coverage = probes.mapIndexed { index, probe ->
        ProbeCoverage(probe.latitude, probe.longitude, PROBE_RADIUS_KM, statuses[index])
    }
    return ProbeAccess(
        loaded = true,
        needsPromo = needsProbePromo(statuses),
        pdfBlocked = isPdfBlocked(siteLatitude, siteLongitude, coverage),
    )
}

// ─── Creation screen: draft probes ───────────────────────────────────────────

data class DraftProbe(val id: String, val number: Int, val latitude: Double, val longitude: Double)

/** Existing probes are drawn on the map but never movable nor removable. */
data class ExistingProbePoint(val uuid: String, val latitude: Double, val longitude: Double)

object ProbeDrafts {
    /** Trial mode: one single probe. */
    fun maxCount(trialMode: Boolean) = if (trialMode) 1 else Int.MAX_VALUE

    /** A tap on the map places a new probe. In trial mode the single probe is replaced. */
    fun add(
        drafts: List<DraftProbe>,
        id: String,
        latitude: Double,
        longitude: Double,
        trialMode: Boolean
    ): List<DraftProbe> {
        if (trialMode) return listOf(DraftProbe(id, 1, latitude, longitude))
        val next = (drafts.maxOfOrNull { it.number } ?: 0) + 1
        return drafts + DraftProbe(id, next, latitude, longitude)
    }

    /** A tap on a draft removes it. Unknown id (e.g. an existing probe): unchanged. */
    fun remove(drafts: List<DraftProbe>, id: String): List<DraftProbe> = drafts.filter { it.id != id }

    /** Only drafts can be moved: an unknown id (existing probe) leaves the list untouched. */
    fun move(drafts: List<DraftProbe>, id: String, latitude: Double, longitude: Double): List<DraftProbe> =
        drafts.map { if (it.id == id) it.copy(latitude = latitude, longitude = longitude) else it }

    fun canMove(drafts: List<DraftProbe>, id: String) = drafts.any { it.id == id }

    /** Numbers are re-compacted for display (1..n) after a removal. */
    fun renumber(drafts: List<DraftProbe>): List<DraftProbe> =
        drafts.mapIndexed { index, probe -> probe.copy(number = index + 1) }
}

/** Distance of a drag in normalised map units (0..1), given the map scale and the full map size in px. */
fun dragDeltaNormalized(dragPx: Float, scale: Double, fullSizePx: Double): Double {
    if (scale <= 0.0 || fullSizePx <= 0.0) return 0.0
    return dragPx.toDouble() / (scale * fullSizePx)
}

/** True when two normalised positions are within [thresholdPx] screen pixels of each other. */
fun isNearOnScreen(
    x1: Double,
    y1: Double,
    x2: Double,
    y2: Double,
    thresholdPx: Double,
    scale: Double,
    fullSizePx: Double
): Boolean {
    val dx = abs(x1 - x2) * scale * fullSizePx
    val dy = abs(y1 - y2) * scale * fullSizePx
    return sqrt(dx * dx + dy * dy) <= thresholdPx
}

/** "43.60° N · 1.44° E" (2 decimals, locale independent). */
fun formatLatLon(latitude: Double, longitude: Double): String {
    fun two(v: Double): String {
        val scaled = round(abs(v) * 100).toLong()
        return "${scaled / 100}.${(scaled % 100).toString().padStart(2, '0')}"
    }
    val ns = if (latitude >= 0) "N" else "S"
    val ew = if (longitude >= 0) "E" else "W"
    return "${two(latitude)}° $ns · ${two(longitude)}° $ew"
}

// ─── Error mapping ───────────────────────────────────────────────────────────

/**
 * Outcome of a probe call shown in the dialog. PaidBySubscription is a success (no payment link needed).
 * The specific values come from the `code` of the API error body (ApiError).
 */
enum class ProbeFailure {
    ServiceUnavailable,
    DemoAlreadyExists,
    NothingToPay,
    PaidBySubscription,
    AccountHasProbes,
    PendingPayment,
    NoDemo,
    InvalidPosition,
    InvalidName,
    TooManyRequests,
    Generic,
}

/** The four lpsmanager routes (POST /api/checkout/probes with or without isDemo, account, upgrade). */
enum class ProbeCall { Create, Demo, Account, Upgrade }

private val ErrorCodeRegex = Regex("""\\?"code\\?"\s*:\s*\\?"([A-Za-z]{1,64})""")
private val ErrorMessageRegex = Regex("""\\?"message\\?"\s*:\s*\\?"([^"\\]{0,300})""")

/** Parsed `{ code, message }` error body. Never throws: any unexpected body gives null. */
data class ParsedApiError(val code: String, val message: String?)

/**
 * Reads the ApiError body leniently. Accepts an object, a JSON string that contains an object (escaped quotes),
 * and returns null for anything else (empty, plain text, a bare JSON string, HTML...).
 */
fun parseApiError(body: String?): ParsedApiError? {
    if (body.isNullOrBlank()) return null
    return try {
        val code = ErrorCodeRegex.find(body)?.groupValues?.get(1) ?: return null
        ParsedApiError(code, ErrorMessageRegex.find(body)?.groupValues?.get(1))
    } catch (_: Throwable) {
        null
    }
}

/** Failure matching an ApiErrorCode, or null for a code the app has no specific message for. */
fun failureForErrorCode(code: String?): ProbeFailure? = when (code?.trim()) {
    "AccountHasProbes" -> ProbeFailure.AccountHasProbes
    "PendingPayment" -> ProbeFailure.PendingPayment
    "NoUnpaidProbe" -> ProbeFailure.NothingToPay
    "NoDemo", "PaidProbesExist" -> ProbeFailure.NoDemo
    "InvalidProbePosition", "InvalidPosition" -> ProbeFailure.InvalidPosition
    "InvalidProbeName" -> ProbeFailure.InvalidName
    "TooManyRequests" -> ProbeFailure.TooManyRequests
    else -> null
}

/**
 * Maps an HTTP error to a failure. The `code` of the ApiError body wins; without a usable code (older body
 * format, unexpected text) the status code and the route are used:
 * - 409 on a demo request: the trial is not available (already used / account already has probes);
 * - 404 / 409 on account / upgrade: nothing to pay (no estimate, no demo, already paid, payment pending);
 * - 429: too many requests; 404 / 501 / 502 / 503 elsewhere: the route is not reachable.
 */
fun classifyProbeFailure(httpStatus: Int?, body: String?, call: ProbeCall = ProbeCall.Create): ProbeFailure {
    failureForErrorCode(parseApiError(body)?.code)?.let { return it }
    return when {
        httpStatus == 429 -> ProbeFailure.TooManyRequests
        httpStatus == 409 && call == ProbeCall.Demo -> ProbeFailure.DemoAlreadyExists
        (httpStatus == 404 || httpStatus == 409) && (call == ProbeCall.Account || call == ProbeCall.Upgrade) ->
            ProbeFailure.NothingToPay
        httpStatus in listOf(404, 501, 502, 503) -> ProbeFailure.ServiceUnavailable
        else -> ProbeFailure.Generic
    }
}

/** The payment link is null (or blank) when the subscription paid automatically. */
fun isPaidBySubscription(paymentLink: String?) = paymentLink.isNullOrBlank()


// ─── Account status (GET /api/account/status) ────────────────────────────────

/** What the app can offer according to `actions`. A null value (route failed) keeps the previous behaviour. */
data class AccountActions(val actions: Set<AccountAction>) {
    fun has(action: AccountAction) = action in actions
}

/** Unknown actions (route failed) never hide a button: the server answers the call anyway. */
fun AccountActions?.allows(action: AccountAction) = this == null || has(action)

fun AccountStatus.accountActions() = AccountActions(actions.mapNotNull { AccountAction.parse(it) }.toSet())

fun isDeletedApiProbe(probe: ApiProbe) = isProbeDeleted(probe.status)

/** Legacy and ExternalContract probes are normal active probes: no trial, no payment, no action. */
fun isManagedSource(source: ProbeSource?) = source == ProbeSource.Legacy || source == ProbeSource.ExternalContract

/**
 * Raw status fields of a probe of the status route, in the form [resolveProbeStatus] understands.
 * - Legacy / ExternalContract: always active (never trial nor unpaid);
 * - demo probe: the end of the trial is `demo.endsAtSecondsUtc` (probe `validUntilSecondsUtc` as fallback);
 *   demo state Expired = expired trial even without date, Active = trial until the end date;
 * - subscription probe: Active = active, Inactive = unpaid; Deleted = deleted.
 */
fun accountProbeInput(probe: ApiProbe, demo: DemoStatus?, nowSeconds: Long): ProbeStatusInput {
    val raw = probe.status.trim().lowercase()
    if (raw == "deleted") return ProbeStatusInput(status = "Deleted")
    if (isManagedSource(probe.probeSource())) return ProbeStatusInput(status = "Active")
    val isDemo = probe.isDemo || probe.probeSource() == ProbeSource.Demo
    if (!isDemo) {
        return ProbeStatusInput(status = if (raw == "active") "Active" else "Inactive")
    }
    val end = demo?.endsAtSecondsUtc ?: probe.validUntilSecondsUtc
    return when (demo?.demoState()) {
        DemoState.Expired -> ProbeStatusInput(
            status = "Inactive",
            isDemo = true,
            // The Expired state wins: never a future end date
            validUntilSecondsUtc = minOf(demo.endsAtSecondsUtc ?: (nowSeconds - 1), nowSeconds - 1),
        )
        DemoState.Active -> ProbeStatusInput(status = "Active", isDemo = true, validUntilSecondsUtc = end)
        null -> ProbeStatusInput(
            status = if (raw == "active") "Active" else "Inactive",
            isDemo = true,
            validUntilSecondsUtc = end,
        )
    }
}

/** Probes to show (deleted ones removed) with their raw status fields. */
fun AccountStatus.visibleProbes(nowSeconds: Long): List<Pair<ApiProbe, ProbeStatusInput>> =
    probes.filterNot { isDeletedApiProbe(it) }.map { it to accountProbeInput(it, demo, nowSeconds) }

/** Card button: the previous rule restricted to what `actions` offers (Upgrade, Pay). Null = previous behaviour. */
fun probeActionFor(input: ProbeStatusInput, status: ProbeStatus, actions: AccountActions?): ProbeAction =
    when (val base = probeAction(input, status)) {
        ProbeAction.Upgrade -> if (actions.allows(AccountAction.Upgrade)) base else ProbeAction.None
        ProbeAction.FinalizePayment -> if (actions.allows(AccountAction.Pay)) base else ProbeAction.None
        ProbeAction.None -> base
    }

/**
 * Failure shown when a trial is requested although the account cannot start one: an existing (or expired)
 * demo = trial already used, otherwise the account already has probes.
 */
fun trialUnavailableFailure(status: AccountStatus?): ProbeFailure =
    if (status?.demo != null) ProbeFailure.DemoAlreadyExists else ProbeFailure.AccountHasProbes

/** Dashboard promo card: which buttons to show. Both false = no card (nothing can be offered). */
data class PromoButtons(val create: Boolean, val trial: Boolean) {
    val visible get() = create || trial
}

fun promoButtons(actions: AccountActions?) =
    PromoButtons(create = actions.allows(AccountAction.AddProbes), trial = actions.allows(AccountAction.StartDemo))
